import React, { useMemo, useState } from "react";
import { apiFetch } from "../../../../apiFetch";
import "./LimitDiscoverability.css";

const LimitDiscoverability = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [searchBy, setSearchBy] = useState("all"); // all | fullName | username | profileSlug | id | loginEmail
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [error, setError] = useState("");

    // 🔽 track which player cards are expanded (details)
    const [expandedMap, setExpandedMap] = useState({}); // { [playerId]: boolean }

    const toggleExpanded = (playerId) => {
        setExpandedMap((prev) => ({ ...prev, [playerId]: !prev[playerId] }));
    };

    const placeholder = useMemo(() => {
        switch (searchBy) {
            case "fullName":
                return "Search by full name (e.g., John Smith)";
            case "username":
                return "Search by username (e.g., megavolto)";
            case "profileSlug":
                return "Search by profileSlug (e.g., john-smith-2004)";
            case "loginEmail":
                return "Search by loginEmail (e.g., user@gmail.com)";
            case "id":
                return "Search by Player ID (e.g., 11)";
            default:
                return "Search by name, username, profileSlug, loginEmail, or ID...";
        }
    }, [searchBy]);

    // 🔎 Search Players
    const searchPlayers = async () => {
        const term = searchTerm.trim();
        if (!term) return;

        try {
            setLoading(true);
            setError("");

            // Backend endpoint: GET /api/admin/players?search=...&searchBy=...
            const res = await apiFetch(
                `/api/admin/players?search=${encodeURIComponent(term)}&searchBy=${encodeURIComponent(
                    searchBy
                )}`,
                { method: "GET" }
            );

            if (!res.ok) throw new Error(await res.text());

            const data = await res.json();
            setPlayers(Array.isArray(data) ? data : []);
            setExpandedMap({}); // reset expansion on new search
        } catch (err) {
            console.error("Search failed:", err);
            setError("Failed to search players.");
            setPlayers([]);
        } finally {
            setLoading(false);
        }
    };

    // 🔄 Toggle Discoverability
    const updateDiscoverability = async (playerId, nextValue) => {
        try {
            setBusyId(playerId);

            const res = await apiFetch(`/api/admin/player/${playerId}/discoverability`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ isDiscoverable: nextValue }),
            });

            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();

            const updatedValue =
                data?.player?.isDiscoverable ?? data?.updatedPlayer?.isDiscoverable ?? nextValue;

            setPlayers((prev) =>
                prev.map((p) => (p.id === playerId ? { ...p, isDiscoverable: updatedValue } : p))
            );
        } catch (err) {
            console.error("Update failed:", err);
            alert("Failed to update discoverability.");
        } finally {
            setBusyId(null);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter") searchPlayers();
    };

    // ✅ helper to render a label/value row
    const Field = ({ label, value }) => (
        <div className="LD__metaItem">
            <span className="LD__metaLabel">{label}</span>
            <span className="LD__metaValue">{value ?? "N/A"}</span>
        </div>
    );

    return (
        <div className="LimitDiscoverability">
            <div className="LD__container">
                <div className="LD__header">
                    <div className="LD__titleWrap">
                        <span className="LD__lock" aria-hidden="true">
                            🔒
                        </span>
                        <div>
                            <h2 className="LD__title">Limit Player Discoverability</h2>
                            <p className="LD__subtitle">
                                Search players and manually hide/show them from Video Feed &amp; Video Pool. Especially
                                players who have already successfully found real world opportunities.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Search Controls */}
                <div className="LD__searchCard">
                    <div className="LD__searchRow">
                        <div className="LD__field">
                            <label className="LD__label">Search By</label>
                            <select className="LD__select" value={searchBy} onChange={(e) => setSearchBy(e.target.value)}>
                                <option value="all">All (Recommended)</option>
                                <option value="fullName">Full Name</option>
                                <option value="username">Username</option>
                                <option value="profileSlug">Profile Slug</option>
                                <option value="loginEmail">Login Email</option>
                                <option value="id">Player ID</option>
                            </select>
                        </div>

                        <div className="LD__field LD__field--grow">
                            <label className="LD__label">Search</label>
                            <input
                                className="LD__input"
                                type="text"
                                placeholder={placeholder}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={onKeyDown}
                            />
                        </div>

                        <button className="LD__btn LD__btn--primary" onClick={searchPlayers} disabled={loading}>
                            {loading ? "Searching..." : "Search"}
                        </button>
                    </div>

                    {error && <p className="LD__error">{error}</p>}

                    <div className="LD__hint">
                        Tip: press <span className="LD__kbd">Enter</span> to search.
                    </div>
                </div>

                {/* Results */}
                <div className="LD__results">
                    {players.length === 0 && !loading ? (
                        <div className="LD__empty">
                            <div className="LD__emptyTitle">No results yet</div>
                            <div className="LD__emptyText">
                                Search a player by <b>username</b>, <b>full name</b>, <b>profileSlug</b>, <b>loginEmail</b>, or{" "}
                                <b>ID</b>.
                            </div>
                        </div>
                    ) : (
                        players.map((player) => {
                            const busy = busyId === player.id;
                            const expanded = !!expandedMap[player.id];

                            // ✅ 5 preview fields (always shown)
                            const previewFields = [
                                { label: "Username", value: player.username ? `@${player.username}` : "N/A" },
                                { label: "Player ID", value: player.id },
                                { label: "Profile Slug", value: player.profileSlug || "N/A" },
                                { label: "Login Email", value: player.loginEmail || player.emailAddress || "N/A" },
                                { label: "VSN Status", value: player.vsnSignedStatus || "N/A" },
                            ];

                            // ✅ extra details (shown only when expanded)
                            const extraFields = [
                                { label: "Country", value: player.country },
                                { label: "Nationality", value: player.nationality },
                                { label: "Residence City", value: player.residenceCity },
                                { label: "Gender", value: player.gender },
                                { label: "DOB", value: player.dob },
                                { label: "Age", value: player.age },
                                { label: "Email Address", value: player.emailAddress },
                                { label: "Current Club", value: player.currentClub },
                                { label: "Club Country", value: player.clubCountry },
                                { label: "Previous Club", value: player.previousClub },
                                { label: "Status", value: player.status },
                                { label: "Primary Position", value: player.primaryPosition },
                                { label: "Secondary Position", value: player.secondaryPosition },
                                { label: "Preferred Foot", value: player.preferredFoot },
                                { label: "Height", value: player.height },
                                { label: "Weight", value: player.weight },
                                { label: "Player Strength", value: player.playerStrength },
                                { label: "YouTube Channel ID", value: player.youtubeChannelId || player.youtube_channel_id },
                                { label: "Uploads Playlist ID", value: player.uploadsPlaylistId || player.uploads_playlist_id },
                                { label: "Last Synced At", value: player.lastSyncedAt || player.last_synced_at },
                                { label: "Last Video Published", value: player.lastVideoPublishedAt || player.last_video_published_at },
                                { label: "Transfermarkt URL", value: player.transfermarktUrl },
                                { label: "Can Edit Transfermarkt", value: String(player.canEditTransfermarkt) },
                                { label: "Parent Email", value: player.parentEmail },
                                { label: "Parent Consent Provided", value: String(player.parentConsentProvided) },
                                { label: "VSN Index URL", value: player.vsnIndexUrl },
                                { label: "Can Edit VSN Index", value: String(player.canEditVsnIndex) },
                                { label: "Firebase UID", value: player.firebaseUid },
                                { label: "Created At", value: player.createdAt },
                                { label: "Updated At", value: player.updatedAt },
                            ];

                            return (
                                <div key={player.id} className="LD__playerCard">
                                    <div className="LD__playerLeft">
                                        <div className="LD__nameRow">
                                            <span className="LD__name">{player.fullName || "Unnamed Player"}</span>

                                            <span className={`LD__pill ${player.isDiscoverable ? "LD__pill--yes" : "LD__pill--no"}`}>
                                                {player.isDiscoverable ? "Discoverable" : "Hidden"}
                                            </span>

                                            <button
                                                type="button"
                                                className="LD__detailsBtn"
                                                onClick={() => toggleExpanded(player.id)}
                                                title="View all player details"
                                            >
                                                {expanded ? "Hide Details" : "See Details"}
                                            </button>
                                        </div>

                                        {/* ✅ Preview fields always visible */}
                                        <div className="LD__meta">
                                            {previewFields.map((f) => (
                                                <Field key={f.label} label={f.label} value={f.value} />
                                            ))}
                                        </div>

                                        {/* ✅ Expanded: show everything */}
                                        {expanded && (
                                            <div className="LD__detailsWrap">
                                                <div className="LD__detailsTitle">All Details</div>
                                                <div className="LD__meta LD__meta--details">
                                                    {extraFields.map((f) => (
                                                        <Field key={f.label} label={f.label} value={f.value} />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="LD__actions">
                                        <button
                                            className="LD__btn LD__btn--danger"
                                            disabled={busy}
                                            onClick={() => updateDiscoverability(player.id, false)}
                                            title="Hide from discovery"
                                        >
                                            {busy ? "Updating..." : "Hide"}
                                        </button>

                                        <button
                                            className="LD__btn LD__btn--outline"
                                            disabled={busy}
                                            onClick={() => updateDiscoverability(player.id, true)}
                                            title="Show in discovery"
                                        >
                                            {busy ? "Updating..." : "Show"}
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

export default LimitDiscoverability;